import Styles from './LinkManager.module.scss'
import { Button } from 'blaze-ui/Button'
import { MobileWizard } from 'components/Wizards/MobileWizards/Layout'
import { ArrowRight } from 'blaze-ui/icons'
import { Trash2 } from 'blaze-ui/icons/20'
import { multiStep } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/multiStep'
import { StepProps } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/Types'
import { useState } from 'react'
import { useField } from 'formik'
import { IconButton } from 'components/IconButton/IconButton'
import { LinkLoadingState } from './components/LinkLoadingState'
import { AddLinkInput } from './components/AddLinkInput'
import { TextValues } from 'components/Wizards/MobileWizards/Types'
import { ActionButtons } from './components/ActionButtons'

interface Props extends StepProps {
  linkOptions: string[]
  setLinkOptions: (options: string[]) => void
  removeLinkOption: (index: number) => void
  isGeneratingLinks: boolean
  generateLinkOptions: (payload: TextValues) => void
  hasLinkError: boolean
}

export const LinkManager = multiStep<Props>(({ schema, initialValues, submit, getSchemaField, ...props }) => {
  const { meta } = schema.describe()
  const { linkOptions, setLinkOptions, removeLinkOption, isGeneratingLinks, generateLinkOptions, hasLinkError } = props
  const [showCustomInput, setShowCustomInput] = useState(false)
  const [, , { setValue: setLinks }] = useField<string[]>('links')

  const addCustomLink = (url: string) => {
    setShowCustomInput(false)
    setLinkOptions([...linkOptions, url])
  }

  const openLink = (url: string, event: React.MouseEvent) => {
    event.preventDefault()
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleSubmit = () => {
    setLinks(linkOptions)
    submit()
  }

  return (
    <>
      <MobileWizard.Content>
        <MobileWizard.Section>
          <MobileWizard.Title>{meta.title}</MobileWizard.Title>
          {meta.subtitle && <MobileWizard.Subtitle>{meta.subtitle}</MobileWizard.Subtitle>}
        </MobileWizard.Section>

        <MobileWizard.Section gap={10}>
          {linkOptions.map((url, index) => (
            <div key={index} className={Styles.linkOption}>
              <div className={Styles.linkBubble}>
                <div className={Styles.linkContent}>
                  <a className={Styles.linkUrl} onClick={(e) => openLink(url, e)} title={url} href={url}>
                    {url}
                  </a>
                </div>
              </div>
              <IconButton className={Styles.deleteButton} onPress={() => removeLinkOption(index)}>
                <Trash2 />
              </IconButton>
            </div>
          ))}
          {showCustomInput && (
            <AddLinkInput
              onSubmit={addCustomLink}
              onCancel={() => {
                setShowCustomInput(false)
              }}
            />
          )}
          {isGeneratingLinks && <LinkLoadingState />}
        </MobileWizard.Section>

        {hasLinkError && (
          <MobileWizard.Section>
            <div className={Styles.errorContainer}>
              <div className={Styles.errorText}>
                We had trouble generating link suggestions. Please try again or add your own links.
              </div>
              <Button variant="secondary" size="md" onPress={() => generateLinkOptions(initialValues)}>
                Try Again
              </Button>
            </div>
          </MobileWizard.Section>
        )}

        {!isGeneratingLinks && !showCustomInput && (
          <ActionButtons
            onPasteOwn={() => setShowCustomInput(true)}
            onGenerateMore={() => generateLinkOptions(initialValues)}
          />
        )}
      </MobileWizard.Content>

      <MobileWizard.ButtonFooter>
        <Button
          size="lg"
          variant="primary"
          endIcon={ArrowRight}
          fullWidth
          onPress={handleSubmit}
          isDisabled={isGeneratingLinks}
        >
          {meta.submitLabel}
        </Button>
      </MobileWizard.ButtonFooter>
    </>
  )
})
